import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useCache } from '@/composables/useCache'
import { ArticleRepository } from '@/infrastructure/repositories/article.repository'
import { createApiClient } from '@/infrastructure/api/client'
import type { Article } from '@/domain/entities/article.entity'
import type { GetArticlesParams } from '@/domain/repositories/article.repository'
import { ErrorHandler } from '@/infrastructure/errors'
import { useErrors } from '../composables/app/useErrors'

const DEFAULT_PAGE_SIZE = 9

/**
 * Store для работы со статьями (список + «показать больше»).
 * Бэкенд не поддерживает cursor/offset — при loadMore запрашиваем с увеличенным limit.
 */
export const useArticleStore = defineStore('article', () => {
  const { error, hasError, setApiError, clearErrors } = useErrors('article')
  const articles = ref<Article[]>([])
  const loading = ref(false)
  /** Текущий лимит (сколько статей показываем / запрашиваем) */
  const currentLimit = ref(DEFAULT_PAGE_SIZE)
  /** Параметры последнего запроса (для loadMore) */
  const lastParams = ref<GetArticlesParams | null>(null)
  const localeArticlesCache = useCache<boolean>()
  const localesWithArticles = localeArticlesCache.cache

  const apiClient = createApiClient({
    baseUrl: useRuntimeConfig().public.NUXT_APP_LANDING_API,
  })
  const articleRepository = new ArticleRepository(apiClient)

  const hasArticles = computed(() => articles.value.length > 0)
  /** Есть ли ещё статьи для подгрузки (если получили ровно currentLimit — возможно, есть ещё) */
  const hasMore = computed(() => articles.value.length >= currentLimit.value)

  /**
   * Загрузить список статей
   */
  async function fetchArticles(params?: GetArticlesParams, append: boolean = false) {
    loading.value = true
    clearErrors()

    const limit = params?.limit ?? DEFAULT_PAGE_SIZE
    if (!append) {
      currentLimit.value = limit
    }

    const requestParams: GetArticlesParams = {
      article_locale: params?.article_locale,
      limit,
    }
    lastParams.value = { ...requestParams }

    try {
      const result = await articleRepository.findMany(requestParams)
      if (append) {
        articles.value = [...articles.value, ...result]
      } else {
        articles.value = result
      }
      return result
    } catch (err) {
      const handledError = ErrorHandler.handle(err)
      setApiError(ErrorHandler.toSerializable(handledError))
      throw handledError
    } finally {
      loading.value = false
    }
  }

  /**
   * Показать больше: запрашиваем следующую «страницу» (увеличиваем limit и перезапрашиваем).
   */
  async function loadMore(limit: number = DEFAULT_PAGE_SIZE) {
    const nextLimit = currentLimit.value + limit
    await fetchArticles(
      { ...lastParams.value, limit: nextLimit },
      false
    )
  }

  function reset() {
    articles.value = []
    currentLimit.value = DEFAULT_PAGE_SIZE
    lastParams.value = null
    loading.value = false
    clearErrors()
  }

  function clearError() {
    clearErrors()
  }

  /**
   * Перевірити, чи є статті для локалі (не змінює основний список articles).
   * Результат кешується в localesWithArticles. Один і той самий запит для локалі не дублюється.
   */
  async function checkLocaleHasArticles(locale: string): Promise<boolean> {
    if (!locale) return false
    return localeArticlesCache.getOrFetch(locale, async () => {
      try {
        const result = await articleRepository.findMany({ article_locale: locale, limit: 1 })
        return result.length > 0
      } catch {
        return false
      }
    })
  }

  return {
    articles,
    loading,
    error,
    currentLimit,
    hasArticles,
    hasError,
    hasMore,
    fetchArticles,
    loadMore,
    reset,
    clearError,
    localesWithArticles,
    checkLocaleHasArticles,
  }
})
